import type Quill from 'quill';
import type {Range} from 'quill/core/selection';

import {isNil} from 'ramda';

import {TextEditorApi, TextEditorSelection} from '../../../types/TextEditorApi';
import {expandTextEditorSelectionToWords} from '../../../utils/expandTextEditorSelectionToWords';
import {normalizeTextEditorSelection} from '../../../utils/normalizeTextEditorSelection';

export function createRichTextEditorApi(editor: Quill, toValue: (html: string) => string) {
  let lastSelection: TextEditorSelection | null = null;

  // Quill's index space: one position per character, `\n` closing every block.
  const charAt = (index: number) => {
    if (index < 0 || index >= editor.getLength()) {
      return null;
    }
    const character = editor.getText(index, 1);

    return character === '' ? null : character;
  };

  const normalizeSelection = (range: Range): TextEditorSelection | null => {
    const normalized = normalizeTextEditorSelection(
      {index: range.index, length: range.length},
      (index) => editor.getText(index, 1) === '\n'
    );

    return isNil(normalized) ? null : expandTextEditorSelectionToWords(normalized, charAt);
  };

  const readLiveSelection = () => {
    const range = editor.getSelection();

    return isNil(range) ? null : normalizeSelection(range);
  };

  const handleSelectionChange = (range: Range | null) => {
    if (isNil(range)) {
      return;
    }

    lastSelection = normalizeSelection(range);
  };

  const handleTextChange = () => {
    lastSelection = null;
  };

  editor.on('selection-change', handleSelectionChange);
  editor.on('text-change', handleTextChange);

  const api: TextEditorApi = {
    getSelection: () => readLiveSelection() ?? lastSelection,
    getContent: (selection) =>
      isNil(selection)
        ? editor.getSemanticHTML()
        : editor.getSemanticHTML(selection.index, selection.length),
    replaceSelection: (selection, content) => {
      const inserted = editor.clipboard.convert({html: content});
      const change = editor
        .getContents(0, 0)
        .retain(selection.index)
        .delete(selection.length)
        .concat(inserted);

      editor.history.cutoff();
      editor.updateContents(change, 'user');
      editor.history.cutoff();
      lastSelection = null;

      return toValue(editor.root.innerHTML);
    },
    focus: () => editor.focus(),
  };

  return {
    api,
    dispose: () => {
      editor.off('selection-change', handleSelectionChange);
      editor.off('text-change', handleTextChange);
    },
  };
}
